import { Link } from 'react-router-dom';
import type { Video } from '@/mocks/videos';

interface VideoCardProps {
  video: Video;
  size?: 'normal' | 'large';
}

export default function VideoCard({ video, size = 'normal' }: VideoCardProps) {
  return (
    <Link
      to={`/article/${video.id}`}
      className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer no-underline"
    >
      <div className={`relative overflow-hidden ${size === 'large' ? 'aspect-[16/7]' : 'aspect-[16/9]'}`}>
        <img
          src={video.thumbnail}
          alt={video.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          title={video.title}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
        <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
          <span className="px-2 py-0.5 bg-black/60 text-white text-xs rounded backdrop-blur-sm">
            {video.duration}
          </span>
          <span className="px-2 py-0.5 bg-primary-500/90 text-white text-xs rounded backdrop-blur-sm">
            {video.quality}
          </span>
        </div>
        {video.isNew && (
          <div className="absolute top-3 left-3">
            <span className="px-2 py-0.5 bg-accent-500 text-white text-xs rounded-full font-medium">
              NEW
            </span>
          </div>
        )}
        <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500/90 text-white">
            <i className="ri-play-fill text-lg"></i>
          </div>
        </div>
      </div>
      <div className="p-3 md:p-4">
        <h3 className="text-sm md:text-base font-semibold text-foreground-900 mb-1.5 line-clamp-1 group-hover:text-primary-600 transition-colors duration-200 font-heading">
          {video.title}
        </h3>
        {size === 'large' && (
          <p className="text-xs text-foreground-500 mb-2 line-clamp-2">{video.description}</p>
        )}
        <div className="flex items-center gap-3 text-xs text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-star-fill text-accent-500 text-xs"></i>
            {video.rating}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line"></i>
            {video.views > 10000 ? `${(video.views / 10000).toFixed(1)}万` : video.views}
          </span>
          <span className="text-foreground-400">{video.year}</span>
        </div>
        <div className="mt-2 flex flex-wrap gap-1">
          {video.tags.slice(0, 3).map((tag) => (
            <span
              key={tag}
              className="px-1.5 py-0.5 text-[10px] bg-background-100 text-foreground-600 rounded"
            >
              {tag}
            </span>
          ))}
        </div>
      </div>
    </Link>
  );
}